import xhrService from '../xhrService';

/**
 * Finish types
 *
 */
export type ComplianceReport = {
  paginationState: Object;
  result: [
    {
      projectName: string;
      inspectionCount: number;
      findingsCount: number;
      avgFindings: number;
      maxDay: number;
      openFindings: number;
      avgFindingDays: number;
      notCompleteInExpectedDays: number;
      complianceCount: number;
      ackCount: number;
    },
  ];
};

export type ProjectActivityLogReport = {
  date: Date;
  activity: string;
  clientName: string;
  clientCode: string;
  subClientName: string;
  projectName: string;
  frequency: string;
  type: string;
  additionalNotes: string;
}[];

export type FindingsDetails = {
  findings: string[];
};

export type IndexApi = {
  divisionId: number;
  pageNumber?: number;
  searchQuery?: string;
  rowsPerPage?: number;
  client?: number[];
  maxDate?: Date;
  minDate?: Date;
  findings?: string;
  showArchived?: boolean;
};

export type ProjectActivityLogProjectFilter = {
  clientIds: number[];
  maxDate?: any;
  minDate?: any;
  projectIds: number[];
  divisionIds: number[];
  filter?: string;
};

export type ProjectActivityLogAreaFilter = {
  divisionIds: number[];
  areaIds: number[];
  maxDate?: any;
  minDate?: any;
  filter: ProjectActivityFilterType;
}

export type ProjectActivityFilterType = 'Project Filter' | 'Area Filter';

export type ProjectsReportFilter = {
  regionIds?: number[];
  divisionIds?: number[];
  clientIds?: number[];
  projectIds?: number[];
};

export type ProjectsReportRow = {
  projectId: number;
  regionName: string | null;
  divisionName: string | null;
  areaName: string | null;
  projectName: string;
  clientName: string | null;
  permittee: string | null;
  projectType: string | null;
  inspectorName: string | null;
  inspectionInterval: string | null;
  latLong: string | null;
  permitNumber: string | null;
  currentAcreage: number | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  workOrder: string | null;
  purchaseOrder: string | null;
  projectConfig: string | null;
};

export type FindingApi<D = string> = (divisionId: number) => Promise<D[]>;

/**
 * Go through types
 */
type ReportApi = {
  index: (param: IndexApi) => Promise<ComplianceReport>;
  getFindings: (divisionId: number) => Promise<FindingsDetails>;
  getWeeklyReportModalData: (divisionId: number) => Promise<any>;
  getWeeklyReport: (param: any) => Promise<any>;
  getProjectActivityLogs: (
    param: ProjectActivityLogProjectFilter | ProjectActivityLogAreaFilter ,
  ) => Promise<ProjectActivityLogReport>;
  downloadInspectionCoverage: (param?: InspectionCoverageFilter) => Promise<Blob>;
  getProjectsReport: (param: ProjectsReportFilter) => Promise<ProjectsReportRow[]>;
  generateHubspotReport: (param: HubspotReportFilters) => Promise<HubspotReportGenerateResponse>;
};

// SW2-2152: filters for the async HubSpot report generation.
export type HubspotReportFilters = {
  scope: 'all' | 'pending' | 'archived';
  range: 'ytd' | 'q1' | 'q2' | 'q3' | 'q4' | 'custom' | 'all_till_date';
  start?: string; // ISO "YYYY-MM-DD", only when range === 'custom'
  end?: string;
};

export type HubspotReportGenerateResponse = {
  exportId: number;
  message: string;
};

// SW2-2351: optional date-range filter for the Account Project Overview export. Both
// "YYYY-MM-DD", both optional — omitting either (or both) leaves that side unbounded,
// matching the report's prior all-time behavior.
export type InspectionCoverageFilter = {
  startDate?: string;
  endDate?: string;
};

const paramsSerializer = (params: any) => {
  return Object.keys(params)
    .map((key) => {
      if (Array.isArray(params[key])) {
        return params[key]
          .map((val: any) => `${key}[]=${encodeURIComponent(val)}`)
          .join('&');
      }

      return `${key}=${encodeURIComponent(params[key])}`;
    })
    .join('&');
};

export const reportApi: ReportApi = {
  index: (param) => {
    const params: {
      s?: string;
      p?: number;
      r?: number;
      client?: number[];
      maxDate?: Date;
      minDate?: Date;
      findings?: string;
      showArchived?: boolean;
    } = {};

    if (param.searchQuery) {
      params.s = param.searchQuery;
    }

    if (param.pageNumber) {
      params.p = param.pageNumber;
    }

    if (param.rowsPerPage) {
      params.r = param.rowsPerPage;
    }

    if (param.client) {
      params.client = param.client;
    }
    if (param.maxDate) {
      params.maxDate = param.maxDate;
    }
    if (param.minDate) {
      params.minDate = param.minDate;
    }
    if (param.findings) {
      params.findings = param.findings;
    }
    if (param.showArchived) {
      params.showArchived = param.showArchived;
    }

    return xhrService
      .get(`/api/divisions/${param.divisionId}/compliance`, {
        params,
        paramsSerializer,
      })
      .then((response) => response.data);
  },

  getWeeklyReportModalData: (divisionId) => {
    return xhrService
      .get(`/api/division/${divisionId}/weeklyReportModalData`)
  },

  getWeeklyReport: (param) => {
    return xhrService
      .post(`/api/division/${param.divisionId}/weeklyReport`, {
        ...param,
      })
      .then((response) => response.data);
  },

  getFindings: (divisionId) => {
    return xhrService
      .get(`/api/divisions/${divisionId}/findingTypes`)
      .then((response) => response.data);
  },
  getProjectActivityLogs: (param:  ProjectActivityLogProjectFilter | ProjectActivityLogAreaFilter) => {
    return xhrService
      .post(`/api/reports/projectActivityLog`, {
        ...param,
      })
      .then((response) => response.data);
  },

  downloadInspectionCoverage: (param) => {
    const params: { startDate?: string; endDate?: string } = {};

    if (param?.startDate) {
      params.startDate = param.startDate;
    }
    if (param?.endDate) {
      params.endDate = param.endDate;
    }

    return xhrService
      .get('/api/reports/inspectionCoverage/download', { params, responseType: 'blob' })
      .then((response) => response.data);
  },

  getProjectsReport: (param: ProjectsReportFilter) => {
    return xhrService
      .post('/api/reports/projects', { ...param })
      .then((response) => response.data);
  },
  // SW2-2152: enqueue async HubSpot report generation; the requester is emailed
  // an access-controlled download link when the background job completes.
  generateHubspotReport: (param) => {
    return xhrService
      .post('/api/reports/hubspot', { ...param })
      .then((response) => response.data);
  },
};

export default reportApi;
